import React, { useState } from 'react';
import { Eye, EyeOff, Key, Lock, Mail, User } from 'lucide-react';
import AuthInput from './AuthInput';
import AuthButton from './AuthButton';
import { validateEmail, validatePassword } from './useAuthActions';

const emptyForm = {
  email: '',
  firstName: '',
  lastName: '',
  inviteKey: '',
  username: '',
  password: '',
};

export default function SignupForm({ onSubmit, loading }) {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [touched, setTouched] = useState({});

  const getFieldError = (field) => {
    if (!touched[field]) return null;
    switch (field) {
      case 'email':
        return formData.email && !validateEmail(formData.email)
          ? 'Please enter a valid email'
          : null;
      case 'password':
        return formData.password && !validatePassword(formData.password)
          ? 'Password must be at least 6 characters'
          : null;
      case 'username':
        return formData.username.length > 0 && formData.username.length < 3
          ? 'Username must be at least 3 characters'
          : null;
      default:
        return null;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({
      email: true,
      password: true,
      username: true,
    });
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-5">
      <AuthInput
        label="Email address"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange}
        onBlur={() => handleBlur('email')}
        placeholder="you@example.com"
        required
        autoComplete="email"
        icon={Mail}
        error={getFieldError('email')}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
        <AuthInput
          label="First name"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          placeholder="Jane"
          required
          autoComplete="given-name"
          icon={User}
        />
        <AuthInput
          label="Last name"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          placeholder="Smith"
          required
          autoComplete="family-name"
          icon={User}
        />
      </div>

      <AuthInput
        label="Invite key"
        name="inviteKey"
        value={formData.inviteKey}
        onChange={handleChange}
        placeholder="Enter your invite key"
        required
        icon={Key}
      />

      <AuthInput
        label="Username"
        name="username"
        value={formData.username}
        onChange={handleChange}
        onBlur={() => handleBlur('username')}
        placeholder="Choose a username"
        required
        autoComplete="username"
        icon={User}
        error={getFieldError('username')}
      />

      <AuthInput
        label="Password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={handleChange}
        onBlur={() => handleBlur('password')}
        placeholder="Create a password"
        required
        autoComplete="new-password"
        icon={Lock}
        error={getFieldError('password')}
        trailing={
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="text-gray-400 transition-colors hover:text-teal-600"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        }
      />

      <AuthButton type="submit" loading={loading}>
        Create account
      </AuthButton>
    </form>
  );
}
